import { Outlet, createFileRoute, redirect } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'

import type { MeQueryData } from '@/hooks/use-auth'
import { AUTH_ME_QUERY_KEY, meQueryFn, useAuth } from '@/hooks/use-auth'
import { usePushAutoPrompt } from '@/hooks/use-push-auto-prompt'
import { useSSENotifications } from '@/hooks/use-sse-notifications'
import { ImpersonationBanner } from '@/components/impersonation-banner'
import { StagingBanner } from '@/components/staging-banner'
import { queryClient } from '@/router'
import { m } from '@/lib/paraglide/messages'

function RouteError({ error, reset }: ErrorComponentProps) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center p-6">
            <p className="text-destructive font-medium">
                {m.error_page_title()}
            </p>
            <p className="text-sm text-muted-foreground max-w-sm">
                {error instanceof Error
                    ? error.message
                    : m.error_page_load_failed()}
            </p>
            <button
                onClick={reset}
                className="text-sm underline text-primary hover:no-underline"
            >
                {m.error_page_retry()}
            </button>
        </div>
    )
}

export const Route = createFileRoute('/_app/_authed')({
    beforeLoad: async ({ location }) => {
        // Ensure auth data is fetched before rendering any protected route.
        // Using beforeLoad (not <Navigate>) prevents infinite re-render loops
        // caused by useRouterState() subscription triggering re-renders during transitions.
        const data = await queryClient.ensureQueryData<MeQueryData>({
            queryKey: AUTH_ME_QUERY_KEY,
            queryFn: meQueryFn,
        })

        if (!data?.user) {
            const redirectTo =
                location.pathname !== '/' && location.pathname !== '/login'
                    ? location.pathname
                    : undefined
            throw redirect({ to: '/login', search: { redirect: redirectTo } })
        }
    },
    component: AuthedLayer,
    errorComponent: RouteError,
})

function AuthedLayer() {
    const { user, isImpersonating } = useAuth()
    useSSENotifications()
    usePushAutoPrompt(!isImpersonating)
    // beforeLoad guarantees user; guards the one-render lag
    if (!user) return null
    return (
        <>
            <StagingBanner />
            <ImpersonationBanner />
            <Outlet />
        </>
    )
}
